import React, { useState } from "react";
import { useSelector } from "react-redux";
import { GitRefSelector } from "~/areas/projects/components/GitRefSelector/GitRefSelector";
import { useProjectContext } from "~/areas/projects/context";
import type { GitBranchResource, GitPersistenceSettings } from "~/client/resources";
import type { GitRef } from "~/client/resources/versionControlledResource";
import { toGitBranch, toGitRefShort } from "~/client/resources/versionControlledResource";
import ToolTip from "~/primitiveComponents/dataDisplay/ToolTip";
import { Snackbar } from "~/primitiveComponents/feedback/Snackbar";
import styles from "./style.module.less";

interface BranchSelectorProps {
    disabled?: boolean;
    vcsHasError?: boolean;
    vcsErrorMessages?: string[];
    onCreateBranch?: (newBranchName: string) => Promise<void>;
}

export function BranchSelector(props: BranchSelectorProps) {
    const projectContext = useProjectContext();
    const project = projectContext.state.model;
    const isPageVersionControlled = useSelector((state: GlobalState) => state.formPaperLayout.isPageVersionControlled);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [newGitRef, setNewGitRef] = useState<string | null>(null);
    const [hasError, setHasError] = useState<boolean>(props.vcsHasError ?? false);

    const onCreateBranch = async (newBranchName: string, parentGitRef: GitRef): Promise<GitBranchResource> => {
        const newBranchResource = await projectContext.state.projectContextRepository.Branches.createBranch(project, newBranchName, parentGitRef);

        await projectContext.actions.onBranchSelected(project, newBranchResource.CanonicalName);
        return newBranchResource;
    };

    const onChange = (gitRef: GitRef) => {
        // Note: When we have a newly selected branch, all we need to do is push it into our router history
        // Note: The props will then flow back into BranchSelector and its initialization useEffect will be triggered
        // Note: As it has a dependency on props.projectContext.state.branch, which in turn changes with there is a new branchName route match
        if (gitRef) {
            projectContext.actions.changeGitRef(gitRef);
            setNewGitRef(toGitRefShort(gitRef));
            setShowSnackbar(true);
        }
    };

    const getInitialGitRef = (): GitRef => {
        if (projectContext.state.gitRef) {
            return projectContext.state.gitRef.CanonicalName;
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const vcs: GitPersistenceSettings = projectContext.state.model.PersistenceSettings as GitPersistenceSettings;
        return toGitBranch(vcs.DefaultBranch);
    };

    const isVersionControlled = projectContext.state.model.IsVersionControlled;
    const emptyElementNecessaryForActionListSpacing = <div />;

    if (!isVersionControlled) return emptyElementNecessaryForActionListSpacing;

    const gitRefSelector = (
        <GitRefSelector
            onChange={onChange}
            project={projectContext.state.model}
            initialGitRef={getInitialGitRef()}
            onCreateBranch={onCreateBranch}
            hasError={props.vcsHasError}
            onError={async (_: Error) => {
                await projectContext.actions.refreshModel();
            }}
            disabled={props.disabled || !isPageVersionControlled}
        />
    );

    if (hasError) {
        return (
            <>
                <ToolTip content="We were unable to connect to your remote repository. Please check your version control settings.">{gitRefSelector}</ToolTip>
                <div className={styles.errorMessage}>Check your version control settings</div>
            </>
        );
    }

    if (!isPageVersionControlled) {
        return (
            <>
                <ToolTip content="Switching branches is only allowed on a version-controlled page">{gitRefSelector}</ToolTip>
                <div className={styles.disabledMessage}>Current page is not version-controlled</div>
            </>
        );
    }

    return (
        <div id="branchSelector" className={styles.selectContainer}>
            <>
                {gitRefSelector}
                <Snackbar
                    open={showSnackbar}
                    autoHideDuration={3500}
                    onClose={() => setShowSnackbar(false)}
                    content={
                        <div className={styles.snackbarContent}>
                            <div className={styles.snackbarMessage}>
                                Switched to <strong>{newGitRef}</strong>
                            </div>
                        </div>
                    }
                />
            </>
        </div>
    );
}
